"use client";

import React from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconZap from "@/app/(components)/generics/Icons/IconZap";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import {
  useBrand,
  useIsCompanyOwner,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { Credits } from "@/app/vars/Credits";
import { Limits } from "@/app/vars/Limits";
import sidebarStyles from "../../Sidebar.module.css";
import styles from "./SidebarCredits.module.css";

type Props = DefaultProps & {
  isOpen: boolean;
};

export default function SidebarCredits(props: Props) {
  const T = useT();
  const { params, isOpen } = props;

  const user = useAuthUser();

  const credits = user ? (user?.processingBalance ?? 0) : Credits.DEFAULT_FREE_CREDITS;
  const isTenant = useBrand() !== null;
  const isCompanyOwner = useIsCompanyOwner();
  const canBuy = !isTenant || isCompanyOwner;

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    ModalStore.getState().openBuyCredits(params);
  };

  if (!isOpen) {
    return (
      <Tooltip
        position={TooltipPosition.RIGHT}
        gap={Limits.SIDEBAR_TOOLTIP_GAP}
        type={TooltipType.GREEN}
        content={`${credits} ${T(TKey.CREDITS_LABEL)}`}
        className={sidebarStyles.fullWidth}
        trigger={
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Button
              min={false}
              params={params}
              isLink={false}
              type={ButtonType.GHOST}
              iconOnly={true}
              full={false}
              beforeIcon={<IconZap />}
              className={sidebarStyles.navButton}
              ariaLabel={`${credits} ${T(TKey.CREDITS_LABEL)}`}
              onClick={handleOpenModal}
            />
          </div>
        }
      />
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.left}>
        <h3 className={styles.title}>{T(TKey.CREDITS_LABEL)}</h3>
        <div className={styles.amountRow}>
          <IconZap size={16} className={styles.icon} />
          <strong className={styles.amount}>{credits}</strong>
        </div>
      </div>
      {canBuy && (
        <button
          className={styles.buyBtn}
          aria-label={T(TKey.CREDITS_BUY)}
          onClick={handleOpenModal}
        >
          <IconPlus size={14} />
        </button>
      )}
    </div>
  );
}
